import {createContext, use, useEffect, useMemo, useReducer, useRef, type ReactNode, type RefObject} from 'react'
import {ssrSafeWindow} from '@github-ui/ssr-utils'

const WIDE_MODE_BREAKPOINT = 1024

interface CopilotSidePanelState {
  isOpen: boolean
  isCompact: boolean
  renderActive: boolean
}

type CopilotSidePanelAction =
  | {type: 'OPEN'; isCompact: boolean}
  | {type: 'CLOSE'}
  | {type: 'TOGGLE'; isCompact: boolean}
  | {type: 'HIDE_FOR_RESIZE'}
  | {type: 'SHOW_ON_RESIZE'}

const initialState: CopilotSidePanelState = {
  isOpen: false,
  isCompact: false,
  renderActive: false,
}

function reducer(state: CopilotSidePanelState, action: CopilotSidePanelAction): CopilotSidePanelState {
  switch (action.type) {
    case 'OPEN':
      return {isOpen: true, isCompact: action.isCompact, renderActive: true}
    case 'CLOSE':
      return initialState
    case 'TOGGLE':
      if (state.isOpen && state.renderActive) return initialState
      return {isOpen: true, isCompact: action.isCompact, renderActive: true}
    case 'HIDE_FOR_RESIZE':
      return {...state, renderActive: false}
    case 'SHOW_ON_RESIZE':
      return {...state, renderActive: true}
  }
}

function measureIsWide(): boolean {
  return ssrSafeWindow ? ssrSafeWindow.innerWidth >= WIDE_MODE_BREAKPOINT : false
}

// --- Contexts ---

export interface CopilotSidePanelActions {
  openPanel: (returnFocusTo?: HTMLElement | null) => void
  closePanel: () => void
  togglePanel: (returnFocusTo?: HTMLElement | null) => void
}

export interface CopilotSidePanelFocusRefs {
  closeButtonRef: RefObject<HTMLButtonElement | null>
  returnFocusRef: RefObject<HTMLElement | null>
}

const CopilotSidePanelStateContext = createContext<CopilotSidePanelState>(initialState)

const defaultActions: CopilotSidePanelActions = {
  openPanel: () => undefined,
  closePanel: () => undefined,
  togglePanel: () => undefined,
}

const CopilotSidePanelActionsContext = createContext<CopilotSidePanelActions>(defaultActions)

const defaultFocusRefs: CopilotSidePanelFocusRefs = {
  closeButtonRef: {current: null},
  returnFocusRef: {current: null},
}

const CopilotSidePanelFocusContext = createContext<CopilotSidePanelFocusRefs>(defaultFocusRefs)

// --- Provider ---

interface CopilotSidePanelProviderProps {
  children: ReactNode
}

/**
 * Standalone provider that manages copilot side panel state.
 * Use this in pages where DockedPanelContext is not available (e.g., CodeView).
 */
export function CopilotSidePanelProvider({children}: CopilotSidePanelProviderProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)
  const [state, dispatch] = useReducer(reducer, initialState)
  const stateRef = useRef(state)

  useEffect(() => {
    stateRef.current = state
  }, [state])

  // Focus the close button when the panel opens in wide mode.
  // Using an effect avoids reading refs during render (React Compiler)
  // and avoids stale-ref issues with memoized callbacks.
  const prevOpenRef = useRef(false)
  useEffect(() => {
    if (state.isOpen && !prevOpenRef.current && measureIsWide()) {
      closeButtonRef.current?.focus()
    }
    prevOpenRef.current = state.isOpen
  }, [state.isOpen, closeButtonRef])

  // Hide the docked panel when the viewport shrinks below the breakpoint.
  // Re-show it when the viewport grows back. This mirrors the PR view's
  // DockedPanelContext behavior so the user must reopen in compact (dialog) mode.
  useEffect(() => {
    let debounceId: ReturnType<typeof setTimeout> | null = null

    function handleResize() {
      if (debounceId !== null) clearTimeout(debounceId)
      debounceId = setTimeout(() => {
        debounceId = null
        const current = stateRef.current
        if (!current.isOpen) return
        const isWide = measureIsWide()
        if (!current.isCompact && !isWide) {
          dispatch({type: 'HIDE_FOR_RESIZE'})
        }
        if (!current.renderActive && isWide) {
          dispatch({type: 'SHOW_ON_RESIZE'})
        }
      }, 150)
    }

    // eslint-disable-next-line github/prefer-observers
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      if (debounceId !== null) clearTimeout(debounceId)
    }
  }, [])

  const actions = useMemo<CopilotSidePanelActions>(
    () => ({
      openPanel: (returnFocusTo?: HTMLElement | null) => {
        if (returnFocusTo) returnFocusRef.current = returnFocusTo
        const isWide = measureIsWide()
        dispatch({type: 'OPEN', isCompact: !isWide})
      },
      closePanel: () => {
        dispatch({type: 'CLOSE'})
      },
      togglePanel: (returnFocusTo?: HTMLElement | null) => {
        if (returnFocusTo) returnFocusRef.current = returnFocusTo
        const isWide = measureIsWide()
        dispatch({type: 'TOGGLE', isCompact: !isWide})
      },
    }),
    [],
  )

  const focusRefs = useMemo<CopilotSidePanelFocusRefs>(() => ({closeButtonRef, returnFocusRef}), [])

  return (
    <CopilotSidePanelStateContext value={state}>
      <CopilotSidePanelActionsContext value={actions}>
        <CopilotSidePanelFocusContext value={focusRefs}>{children}</CopilotSidePanelFocusContext>
      </CopilotSidePanelActionsContext>
    </CopilotSidePanelStateContext>
  )
}

// --- Bridge provider for PR's DockedPanelContext ---

interface CopilotSidePanelBridgeProviderProps {
  children: ReactNode
  isOpen: boolean
  isCompact: boolean
  closePanel: () => void
  closeButtonRef: RefObject<HTMLButtonElement | null>
  returnFocusRef: RefObject<HTMLElement | null>
}

/**
 * Bridge provider that maps external panel state (e.g., from DockedPanelContext)
 * to the shared copilot side panel context. Use this in pages where panel state
 * is managed by an external context (e.g., pull-requests).
 */
export function CopilotSidePanelBridgeProvider({
  children,
  isOpen,
  isCompact,
  closePanel,
  closeButtonRef,
  returnFocusRef,
}: CopilotSidePanelBridgeProviderProps) {
  const state = useMemo<CopilotSidePanelState>(() => ({isOpen, isCompact, renderActive: isOpen}), [isOpen, isCompact])

  const actions = useMemo<CopilotSidePanelActions>(
    () => ({
      openPanel: () => undefined,
      closePanel,
      togglePanel: () => undefined,
    }),
    [closePanel],
  )

  const focusRefs = useMemo<CopilotSidePanelFocusRefs>(
    () => ({closeButtonRef, returnFocusRef}),
    [closeButtonRef, returnFocusRef],
  )

  return (
    <CopilotSidePanelStateContext value={state}>
      <CopilotSidePanelActionsContext value={actions}>
        <CopilotSidePanelFocusContext value={focusRefs}>{children}</CopilotSidePanelFocusContext>
      </CopilotSidePanelActionsContext>
    </CopilotSidePanelStateContext>
  )
}

// --- Hooks ---

export function useCopilotSidePanelState(): CopilotSidePanelState {
  return use(CopilotSidePanelStateContext)
}

export function useCopilotSidePanelActions(): CopilotSidePanelActions {
  return use(CopilotSidePanelActionsContext)
}

export function useCopilotSidePanelFocusRefs(): CopilotSidePanelFocusRefs {
  return use(CopilotSidePanelFocusContext)
}

export function useIsCopilotSidePanelOpen(): boolean {
  const state = use(CopilotSidePanelStateContext)
  return state.isOpen && state.renderActive
}

export function useIsCopilotSidePanelCompact(): boolean {
  return use(CopilotSidePanelStateContext).isCompact
}

// Re-export context for the bridge provider usage in tests
export {CopilotSidePanelStateContext, CopilotSidePanelActionsContext, CopilotSidePanelFocusContext}
